import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { Users, Play, Check, ArrowRight } from "lucide-react";
import { whyUsSections } from "@/data/whyUsData";

const WhyUsPage = () => {
  return (
    <div data-testid="why-us-page" className="min-h-screen bg-[#050505]">
      <SEO
        title="Why Us | Internal Communications Partner for Large Organisations"
        description="ICA operates as a fully integrated extension of your internal communications team. We help organisations with large workforces improve staff retention, onboarding and employee engagement through strategy, video, design and ongoing support."
        keywords="about internal comms agency, ICA, internal communications partner, employee communications team, bespoke internal comms, strategic communications partner, large organisations, staff retention, employee engagement"
        canonical="/why-us"
      />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center" data-testid="why-us-hero">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#050505]" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#00E5FF]/5 rounded-full blur-[200px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#00E5FF]/3 rounded-full blur-[150px]" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10 pt-32">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#00E5FF]" />
              </div>
              <span className="text-[#00E5FF] font-semibold uppercase tracking-wider">
                Why Us
              </span>
            </div>

            <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-none text-white mb-6">
              WHY CHOOSE
              <br />
              <span className="text-[#00E5FF]">ICA</span>
            </h1>

            <p className="text-lg md:text-xl text-[#B3B3B3] max-w-3xl mb-8">
              Your organisation already has the training, values and policies — but your people don't know about them. 
              We bridge that gap. ICA operates as a fully integrated extension of your communications team, 
              ensuring every employee actually sees, understands and acts on the information that matters.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  data-testid="why-us-book-call-btn"
                  className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-6 uppercase tracking-wider text-base"
                >
                  <Play className="w-5 h-5 mr-2 fill-black" />
                  Book a Call
                </Button>
              </Link>
              <Link to="/case-studies">
                <Button
                  data-testid="why-us-case-studies-btn"
                  variant="outline"
                  className="bg-white/10 text-white hover:bg-white/20 border-white/10 px-8 py-6 uppercase tracking-wider text-base"
                >
                  View Our Work
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sections */}
      <div data-testid="why-us-sections">
        {whyUsSections.map((section, index) => (
          <section
            key={section.id}
            data-testid={`why-us-section-${section.id}`}
            className="py-24 md:py-32"
            style={{ backgroundColor: index % 2 === 0 ? '#050505' : '#0a0a0a' }}
          >
            <div className="container mx-auto px-4 md:px-8">
              <div className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-16 ${section.imageContain ? 'items-center' : 'items-start'}`}>
                {/* Image */}
                <div className="w-full lg:w-1/2">
                  {section.image ? (
                    <div className={`relative ${section.imageContain ? '' : 'aspect-video'} rounded-lg overflow-hidden`}>
                      <img
                        src={section.image}
                        alt={section.title}
                        className={`w-full ${section.imageContain ? 'max-h-[500px] object-contain' : 'h-full object-cover'}`}
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/10 to-transparent" />
                    </div>
                  ) : (
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-[#0a0a0a] border border-[#1F1F1F] flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-20 h-20 rounded-xl bg-[#00E5FF]/10 flex items-center justify-center mx-auto mb-4">
                          <section.icon className="w-10 h-10 text-[#00E5FF]" />
                        </div>
                        <span className="text-[#00E5FF]/30 font-display text-6xl tracking-tight">{section.number}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="w-full lg:w-1/2">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-[#00E5FF]/30 font-display text-4xl md:text-5xl tracking-tight">
                      {section.number}
                    </span>
                    <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center">
                      <section.icon className="w-5 h-5 text-[#00E5FF]" />
                    </div>
                  </div>

                  <h2 className="font-display text-2xl md:text-3xl tracking-wide text-white mb-5">
                    {section.title}
                  </h2>

                  <p className="text-[#B3B3B3] text-lg leading-relaxed mb-6">
                    {section.description}
                  </p>

                  {section.capabilities && (
                    <ul className="space-y-3 mb-6">
                      {section.capabilities.map((cap, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded-full bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-[#00E5FF]" />
                          </div>
                          <span className="text-white">{cap}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="bg-[#0a0a0a] border border-[#1F1F1F] rounded-xl p-6 hover:border-[#00E5FF]/20 transition-colors">
                    {section.heading && (
                      <h3 className="font-display text-lg tracking-wide text-[#00E5FF] mb-4">
                        {section.heading}
                      </h3>
                    )}
                    <ul className="space-y-3">
                      {section.points.map((point, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <ArrowRight className="w-4 h-4 text-[#00E5FF] flex-shrink-0 mt-1" />
                          <span className="text-[#B3B3B3]">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>

      {/* Yellow Agency Note */}
      <section className="py-16 bg-[#0a0a0a]" data-testid="why-us-yellow-agency">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#B3B3B3] text-lg leading-relaxed">
              ICA (The Internal Comms Agency) is the dedicated internal communications division of media company{" "}
              <a 
                href="https://www.yellowagency.co.uk" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#00E5FF] hover:underline"
              >
                The Yellow Agency
              </a>
              , bringing the same creative excellence and strategic thinking to transform how organisations communicate with their people.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#050505] relative overflow-hidden" data-testid="why-us-cta">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#00E5FF] rounded-full blur-[150px]" />
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-6">
              READY TO TRANSFORM YOUR
              <br />
              <span className="text-[#00E5FF]">INTERNAL COMMUNICATIONS?</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg mb-8">
              Let's discuss how ICA can become an extension of your team and deliver communications that truly connect with your people.
            </p>
            <Link to="/contact">
              <Button
                data-testid="why-us-cta-btn"
                className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-10 py-6 uppercase tracking-wider text-base"
              >
                Start the Conversation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyUsPage;
